/**
 * Random Game Modal Component
 * Displays a randomly selected game with slot machine-style rolling effect
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type IGameSettings from '../../../../types/models/IGameSettings';
import Game from '../../../games/components/GameCard';
import { useGameStartActions } from '../../../games/hooks/useGameStart';
import AutoTextfit from '../../../../shared/components/AutoTextfit';
import { clickCounter } from '../../../../core/services/EventTracker';
import { transactionTracker } from '../../../../core/services/TransactionTracker';
import { useSeasonalBranding } from '../../../../store/featureFlags/featureFlagsStore';import { RANDOM_GAME_BRANDINGS, type CardRevealProps } from './brandings';
import styles from './RandomGameModal.module.css';

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path d="M19 3L3 19M3 3L19 19" stroke="#CF001C" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RollingPlaceholder = ({ label }: { label: string }) => (
  <div className={styles.rollingPlaceholder}>
    <div className={styles.questionMarks}>
      <span className={styles.questionMark}>?</span>
      <span className={styles.questionMark}>?</span>
      <span className={styles.questionMark}>?</span>
    </div>
    <div className={styles.rollingText}>{label}</div>
  </div>
);

const PlainReveal: React.FC<CardRevealProps> = ({ children }) => <>{children}</>;

// Mirrors the (max-height: 650px) rule in RandomGameModal.module.css that shrinks the title to 80%.
const COMPACT_QUERY = '(max-height: 650px)';
const TITLE_MAX_FONT_SIZE_PX = 30;
const TITLE_COMPACT_MAX_FONT_SIZE_PX = TITLE_MAX_FONT_SIZE_PX * 0.8;
const TITLE_MIN_FONT_SIZE_PX = 16;

const useIsCompact = () => {
  const [isCompact, setIsCompact] = useState(() => window.matchMedia(COMPACT_QUERY).matches);

  useEffect(() => {
    const mql = window.matchMedia(COMPACT_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsCompact(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isCompact;
};

interface RandomGameModalProps {
  allGames: IGameSettings[];
  onClose: () => void;
}

export const RandomGameModal: React.FC<RandomGameModalProps> = ({ allGames, onClose }) => {
  const { t } = useTranslation();
  const [displayedGame, setDisplayedGame] = useState<IGameSettings | null>(null);
  const [isRolling, setIsRolling] = useState(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { startGame } = useGameStartActions();
  const seasonalBranding = useSeasonalBranding();
  const isTrickOrTreat = seasonalBranding === 'halloween';
  const isCompact = useIsCompact();
  // Seasonal brandings theme the modal and wrap the card in their own reveal; otherwise plain.
  const branding = seasonalBranding ? RANDOM_GAME_BRANDINGS[seasonalBranding] : undefined;
  const CardReveal = branding?.Reveal ?? PlainReveal;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Function to get a random game
  const getRandomGame = useCallback(() => {
    if (allGames.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * allGames.length);
    return allGames[randomIndex];
  }, [allGames]);

  // Rolling animation - show placeholder then reveal game
  const startRolling = useCallback(() => {
    setIsRolling(true);
    setDisplayedGame(null);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Show placeholder for a moment, then reveal the chosen game. Trick or Treat waits for
    // the two knocks on the door before it opens.
    const rollingDuration = isTrickOrTreat ? 900 : 400;

    timeoutRef.current = setTimeout(() => {
      setDisplayedGame(getRandomGame());
      setIsRolling(false);
    }, rollingDuration);
  }, [getRandomGame, isTrickOrTreat]);

  // Start rolling on mount
  useEffect(() => {
    startRolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTryAgain = useCallback(() => {
    if (!isRolling) {
      startRolling();
    }
  }, [isRolling, startRolling]);

  const handlePlayNow = useCallback(() => {
    if (!isRolling && displayedGame) {
      clickCounter.incrementClick(`click_game_${displayedGame.gameKey}_single_pickrandom`);
      transactionTracker.trackGameOpen(displayedGame.name, displayedGame.gameKey, 'pickRandom', 'single');
      startGame(displayedGame.gameKey);
      onClose();
    }
  }, [isRolling, displayedGame, startGame, onClose]);

  const title = isTrickOrTreat
    ? t('search.trick_or_treat_title', 'Trick or Treat?')
    : t('search.pick_random', 'Pick A Random Game');
  const subtitle = isTrickOrTreat
    ? isRolling
      ? t('search.trick_or_treat_subtitle', 'Knock and see who answers')
      : t('search.trick_or_treat_reveal', 'A treat, just for you')
    : t('search.random_subtitle', 'We picked a random game just for you');
  const rollingLabel = isTrickOrTreat
    ? t('search.trick_or_treat_knocking', 'Knocking...')
    : t('search.choosing_random', 'Choosing...');
  const tryAgainLabel = isTrickOrTreat
    ? t('search.trick_or_treat_again', 'Knock again')
    : t('search.try_again', 'Try Again');

  const cardContent = isRolling ? (
    <RollingPlaceholder label={rollingLabel} />
  ) : displayedGame ? (
    <Game data={displayedGame} sectionName="pickRandom" viewMode="single" />
  ) : (
    <div className={styles.gameCardPlaceholder} />
  );

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${branding?.themeClassName ?? ''}`}>
        <button className={styles.closeButton} onClick={onClose} disabled={isRolling}>
          <CloseIcon />
        </button>

        <h2 className={styles.title}>
          <AutoTextfit
            maxFontSize={isCompact ? TITLE_COMPACT_MAX_FONT_SIZE_PX : TITLE_MAX_FONT_SIZE_PX}
            minFontSize={TITLE_MIN_FONT_SIZE_PX}
          >
            {title}
          </AutoTextfit>
        </h2>
        <p className={styles.subtitle}>{subtitle}</p>

        <div className={styles.gameCardContainer}>
          <CardReveal open={!isRolling}>{cardContent}</CardReveal>
        </div>

        <div className={`${styles.actions} ${isRolling ? styles.actionsDisabled : ''}`}>
          <button className={styles.playNowButton} onClick={handlePlayNow} disabled={isRolling}>
            {t('search.play_now', 'Play Now')}
          </button>
          <button className={styles.tryAgainButton} onClick={handleTryAgain} disabled={isRolling}>
            {tryAgainLabel}
          </button>
        </div>
      </div>
    </div>,
    document.getElementById('lobby2-root') || document.body,
  );
};
